import { computed, onScopeDispose, ref, watch, type Ref } from 'vue';
import { MIN_QUERY_LENGTH, SEARCHED_ENTITY_IDS, type QuickSearchResponse } from '@/components/commandPalette/groupQuickSearchResults';
import { useQuickSearchSelectionStore } from '@/stores/quickSearchSelectionStore';
import type { QuickSearchSelection } from '@/utils/quickSearchFields';

/**
 * Sends Cmd+K's search to `POST /api/quick_search` (tabasco#825).
 *
 * - Nothing is sent below two characters.
 * - It waits 200 ms after the last key, and a new search cancels the one before.
 * - Each record type is searched on the person's own fields and Matches all or Matches
 *   any (SAU-467), the same choice its list search box uses.
 * - Each answer is kept under the text and the field choice it was for, so a slow answer
 *   for "ra", or for the fields before a tag was turned off, never replaces the newer one.
 * - Two seconds with no answer counts as a failure: the palette keeps its
 *   commands and says search is unavailable.
 * - `searching` is true from the moment the text is long enough until the answer
 *   or the failure arrives, so the palette can say it is still looking rather
 *   than "Nothing matches".
 */

export const SEARCH_DELAY_MS = 200;
export const SEARCH_TIMEOUT_MS = 2000;

/** Each record type's fields and mode, as the request carries them. */
export type QuickSearchFieldsByEntity = Record<string, QuickSearchSelection>;

export type QuickSearchPost = (
    query: string,
    signal: AbortSignal,
    timeoutMs: number,
    fields: QuickSearchFieldsByEntity,
) => Promise<QuickSearchResponse>;

export async function httpQuickSearch(
    query: string,
    signal: AbortSignal,
    timeoutMs: number,
    fields: QuickSearchFieldsByEntity,
): Promise<QuickSearchResponse> {
    const { default: http } = await import('@/services/http');
    const response = await http.post<QuickSearchResponse>('/api/quick_search', { q: query, fields }, { signal, timeout: timeoutMs });
    return response.data;
}

export interface QuickSearchState {
    response: Ref<QuickSearchResponse | null>;
    failed: Ref<boolean>;
    /** A 429: too many searches in a minute. */
    rateLimited: Ref<boolean>;
    /** A search is waiting out its pause or on the wire. */
    searching: Ref<boolean>;
}

export function useQuickSearch(query: Ref<string>, post: QuickSearchPost = httpQuickSearch): QuickSearchState {
    const selectionStore = useQuickSearchSelectionStore();
    const response = ref<QuickSearchResponse | null>(null);
    const failed = ref(false);
    const rateLimited = ref(false);
    const searching = ref(false);
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    const fieldsByEntity = computed<QuickSearchFieldsByEntity>(() =>
        Object.fromEntries(SEARCHED_ENTITY_IDS.map((entityId) => [entityId, selectionStore.selectionFor(entityId)])),
    );
    /** What an answer is for: the text and every record type's fields. */
    const searchKey = computed(() => JSON.stringify([query.value.trim(), fieldsByEntity.value]));

    watch(searchKey, () => {
        const text = query.value.trim();
        const key = searchKey.value;
        const fields = fieldsByEntity.value;
        if (timer) clearTimeout(timer);
        controller?.abort();
        controller = null;
        response.value = null;
        failed.value = false;
        rateLimited.value = false;
        searching.value = false;
        if (text.length < MIN_QUERY_LENGTH) {
            return;
        }
        searching.value = true;
        timer = setTimeout(async () => {
            const current = new AbortController();
            controller = current;
            try {
                const answer = await post(text, current.signal, SEARCH_TIMEOUT_MS, fields);
                if (searchKey.value === key) {
                    response.value = answer;
                    searching.value = false;
                }
            } catch (error) {
                if (current.signal.aborted || searchKey.value !== key) {
                    return;
                }
                const status = (error as { response?: { status?: number } }).response?.status;
                rateLimited.value = status === 429;
                failed.value = true;
                searching.value = false;
            }
        }, SEARCH_DELAY_MS);
    });

    // Leaving the page drops a search still waiting out its pause, and aborts one on the wire.
    onScopeDispose(() => {
        if (timer) clearTimeout(timer);
        controller?.abort();
    });

    return { response, failed, rateLimited, searching };
}
